import { RADIUS_DECIMALS } from '@/lib/maps/circle';
import type { MapPoint } from '@/lib/maps/types';

import { DUPLICATE_POINT_MESSAGE } from './check-placement';
import { MIN_DRAWN_POINTS } from './use-field-points';

/** Which half of a pair a cell holds. `lon` throughout, per the `MapPoint` spelling — the wire's
 *  `lng` is renamed once, at the parse boundary, and never leaks this far (spec § Write path). */
export type CoordinateAxis = 'lat' | 'lon';

/** One **draft** row. Strings, not numbers: a row mid-typing is `'-'`, and a row the Grower has not
 *  reached is `''` — neither of which a number can hold. */
export type CoordinateRow = { lat: string; lon: string };

/** The inline message shown against a cell, per axis. Absent means the cell is not in error. */
export type RowErrors = Partial<Record<CoordinateAxis, string>>;

export const emptyRow = (): CoordinateRow => ({ lat: '', lon: '' });

/**
 * How many rows the panel lays out with nothing marked (113601). Deliberately the **same** constant
 * as the shape's own floor rather than a second 3: the panel opens at the minimum Field, and the two
 * can never disagree about what that is.
 */
export const EMPTY_ROW_COUNT = MIN_DRAWN_POINTS;

/** Verbatim from the criteria. Figma `594:532581` renders a double space before the parenthesis;
 *  ACs beat designs (grilling Q7), so it is one space here. */
export const COORDINATE_HELPER_TEXT =
  'Enter coordinates in decimal degrees (e.g., 40.7128, -74.0060).';

/** Figma leaves `594:532933` / `594:532934` — the exact inline copy for a value out of range. The
 *  same message is shown for an EMPTY cell on an attempted save: "exactly as an out-of-range
 *  value" is what the criterion says. */
export const LATITUDE_MESSAGE = 'Enter a valid latitude (-90 to 90).';
export const LONGITUDE_MESSAGE = 'Enter a valid longitude (-180 to 180).';

/** Re-exported under the row vocabulary so the panel need not reach into `check-placement`, which is
 *  about the map surface. One string, one home — the backend's `FIELD_DUPLICATE_POINT` copy. */
export const DUPLICATE_ROW_MESSAGE = DUPLICATE_POINT_MESSAGE;

const LIMIT: Record<CoordinateAxis, number> = { lat: 90, lon: 180 };

export const rangeMessage = (axis: CoordinateAxis): string =>
  axis === 'lat' ? LATITUDE_MESSAGE : LONGITUDE_MESSAGE;

/**
 * What a Lat or Long input is allowed to contain: digits, **one leading minus**, **one** decimal
 * point. Everything else is dropped **silently** — no error — because the criterion is about pasting
 * a spreadsheet cell, and an error for a degree symbol the Grower did not type would be noise.
 *
 * The extra decimal points are FOLDED rather than truncated (`40..71.28` → `40.7128`): a Grower who
 * fat-fingers a second dot has still told us every digit they meant, and silently binning the tail
 * would change their number instead of their punctuation.
 *
 * A leading `-` **or** `+` survives, and only in the leading position — "a single leading sign" is
 * what the criterion says, and `Number('+40')` needs no help. Leading whitespace is trimmed before
 * the sign is read, so a pasted `" -88.1"` keeps its minus.
 */
export function sanitizeCoordinate(raw: string): string {
  const [first] = raw.trimStart();
  const sign = first === '-' || first === '+' ? first : '';
  const [whole = '', ...fraction] = raw.replace(/[^\d.]/g, '').split('.');
  const decimals = fraction.join('');
  return `${sign}${whole}${fraction.length > 0 ? `.${decimals}` : ''}`;
}

/**
 * The cell's value as a coordinate, or `null` if it is not one.
 *
 * The **empty check comes first, and it must**: `Number('')` is `0`, so a blank Lat would otherwise
 * parse as a perfectly valid coordinate on the equator and a whole empty row would look placeable.
 */
export function parseCoordinate(value: string, axis: CoordinateAxis): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || Math.abs(parsed) > LIMIT[axis]) return null;
  return parsed;
}

/**
 * The rows a **committed shape** implies — what the panel shows whenever the draft is clean.
 *
 * **Full precision, `String()` — never `formatCoordinate`.** A row is an editable value, not a
 * readout: a row the Grower never touches is re-plotted verbatim by the panel's own `Save`, so
 * rounding here would silently move a **Point** they marked by clicking up to ~1 m, and could turn
 * an accepted placement into a refused one. The tooltip rounds because it only ever displays.
 */
export function rowsFromPoints(points: readonly MapPoint[]): CoordinateRow[] {
  if (points.length === 0) return Array.from({ length: EMPTY_ROW_COUNT }, emptyRow);
  return points.map((point) => ({ lat: String(point.lat), lon: String(point.lon) }));
}

/** The draft as a shape, or `null` if any cell is empty or out of range. All-or-nothing: a partial
 *  shape is not a thing the canvas can draw, and `Save` is gated on getting an array back. */
export function rowsToPoints(rows: readonly CoordinateRow[]): MapPoint[] | null {
  const points: MapPoint[] = [];
  for (const row of rows) {
    const lat = parseCoordinate(row.lat, 'lat');
    const lon = parseCoordinate(row.lon, 'lon');
    if (lat === null || lon === null) return null;
    points.push({ lat, lon });
  }
  return points;
}

/**
 * Which rows repeat a pair an earlier row already holds — the **later** one every time, which is
 * what the criterion flags.
 *
 * Keyed on the **parsed numbers**, not the typed strings, so `40.10` and `40.1` are correctly one
 * corner. Rows that do not fully parse are skipped: a half-typed row is not yet a duplicate of
 * anything, and flagging it would fire an error at every keystroke.
 */
export function duplicateRowIndexes(rows: readonly CoordinateRow[]): Set<number> {
  const seen = new Set<string>();
  const duplicates = new Set<number>();
  rows.forEach((row, index) => {
    const lat = parseCoordinate(row.lat, 'lat');
    const lon = parseCoordinate(row.lon, 'lon');
    if (lat === null || lon === null) return;
    // The parsed numbers, NOT a fixed-precision rendering: rounding to 5 places would call two
    // corners a metre apart the same corner — stricter than the server, and stricter than
    // `use-field-points.ts`'s own `isDuplicate`, which compares with `===`.
    const key = `${lat},${lon}`;
    if (seen.has(key)) duplicates.add(index);
    else seen.add(key);
  });
  return duplicates;
}

/**
 * What a **Radius** input is allowed to contain: digits and at most one decimal point, capped at two
 * decimal places. Everything else is dropped **silently**, the same contract
 * {@link sanitizeCoordinate} has and for the same reason — pasting a spreadsheet cell should not
 * raise an error about a character the Grower did not type.
 *
 * Two differences from {@link sanitizeCoordinate}, and both are what a Radius is:
 *
 * - **no sign survives, leading or otherwise** — a Radius is a distance, so `-100` is `100`;
 * - **the fraction is TRUNCATED at two places, not folded** — the digits past the second are
 *   precision the contract does not accept, where a coordinate's extra decimal points were
 *   punctuation the Grower fat-fingered.
 *
 * A trailing `'1.'` is kept as typed: it is the state every "1.5" passes through, and rewriting it
 * mid-keystroke would fight the Grower's typing.
 */
export function sanitizeRadiusInput(raw: string): string {
  const stripped = raw.replace(/[^\d.]/g, '');
  const firstDot = stripped.indexOf('.');
  if (firstDot === -1) return stripped;
  const whole = stripped.slice(0, firstDot);
  const fraction = stripped.slice(firstDot + 1).replace(/\./g, '');
  return `${whole}.${fraction.slice(0, RADIUS_DECIMALS)}`;
}

/**
 * The cell's value as a **Radius**, or `null` if it is not a number at all.
 *
 * **Deliberately does not answer the range** — `isRadiusInRange` does. Blur has to tell an
 * out-of-range value (which shows the message) from an empty cell (which stays silent until Save),
 * and folding the two into one `null` would lose that distinction.
 *
 * The empty check comes first for the same reason it does in {@link parseCoordinate}: `Number('')`
 * is `0`, which would read as a real, merely-out-of-range radius.
 */
export function parseRadius(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
